<script lang="ts">
    import Card from '@smui/card';
    import Select, { Option } from '@smui/select';
    import Input from '@smui/textfield';
    import Button, { Label } from '@smui/button';
    import Checkbox from '@smui/checkbox';
    import FormField from '@smui/form-field';
    import { _ } from 'svelte-i18n';
    import { push } from 'svelte-spa-router';
    import { onMount } from 'svelte';
    import ContainerLoading from '../../../..//shared/components/common/Loading/ContainerLoading.svelte';
    import FormLoading from '../../../..//shared/components/common/Loading/FormLoading.svelte';
    import TitleCard from '../../../..//shared/components/common/Title.svelte';
    import ValoreRispostaQuestionario from '../../../../shared/models/Questionario/ValoreRispostaQuestionario';
    import Questionario from '../../../../shared/models/Questionario/Questionario';
    import { hasRoleSysAdmin } from './../../../../shared/stores';
    import { success, warning, failure } from '../../../../shared/components/common/Toast/Toast';
    import ConfirmModal from '../../../../shared/components/common/Modal/ConfirmModal.svelte';

    export let params = {
        questionarioId: '0',
        tipo: 'SmartWorking' // o 'PSCL'
    };

    let [dataQuestionario, loadingQuestionario, errorQuestionario] = Questionario.get(params.questionarioId);
    let [data, loading, error] = ValoreRispostaQuestionario.getList({
        questionarioId: parseInt(params.questionarioId),
        tipoValore: params.tipo
    });
    
    let [dataSubmit, loadingSubmit, errorSubmit] = [null, null, null];

    let valoriEreditati = [];
    let usingInherited = true;
    
    let opzioniQuestionario = {};
    let opzioniCaricate = false;
    let loadingOpzioni = false;
    let domandaNome = '';
    
    // Modal per conferma ripristino valori ereditati
    let openConfirmResetDialog = false;
    
    async function caricaOpzioniQuestionario() {
        // Evita chiamate multiple se le opzioni sono già state caricate
        if (opzioniCaricate && Object.keys(opzioniQuestionario).length > 0) return;
        
        loadingOpzioni = true;
        try {
            const response = await fetch(`api/valorerisposta/opzioniquestionario/${params.questionarioId}/${params.tipo}`);
            const result = await response.json();
            
            if (result.isError) {
                failure("Errore nel caricamento delle opzioni dal questionario");
                opzioniQuestionario = {};
            } else {
                opzioniQuestionario = result.response || {};
                
                // Inizializza i valori se necessario
                if (!usingInherited && (!$data.response || $data.response.length === 0)) {
                    inizializzaValoriDaOpzioni();
                }
                
                // Segna le opzioni come caricate
                opzioniCaricate = true;
            }
        } catch (error) {
            console.error("Errore nel caricamento delle opzioni:", error);
            opzioniQuestionario = {};
        } finally {
            loadingOpzioni = false;
        }
    }
    
    function inizializzaValoriDaOpzioni() {
        if (Object.keys(opzioniQuestionario).length === 0) return;
        
        // Se non ci sono valori configurati, crea una lista vuota
        if (!$data.response) {
            $data.response = [];
        }
        
        // Verifica quali opzioni mancano dai valori configurati
        const valoriEsistenti = new Set($data.response.map(v => v.rispostaValore));
        const opzioniMancanti = Object.keys(opzioniQuestionario).filter(opzione => !valoriEsistenti.has(opzione));
        
        // Aggiungi solo le opzioni mancanti
        if (opzioniMancanti.length) {
            opzioniMancanti.forEach(opzione => {
                $data.response.push(new ValoreRispostaQuestionario({
                    id: 0,
                    questionarioId: parseInt(params.questionarioId),
                    domandaNome: domandaNome || params.tipo, // Usa il tipo come fallback se domandaNome non è disponibile
                    tipoValore: params.tipo,
                    rispostaValore: opzione,
                    fattoreConversione: 0,
                    dataInsert: new Date(),
                    utenteInsert: '',
                    dataLastUpdate: new Date(),
                    utenteLastUpdate: '',
                    isOverride: true
                }));
            });
            
            // Forza l'aggiornamento
            $data = {...$data};
        }
    }
    
    function submit() {
        if (!$data.response || $data.response.length === 0) {
            failure("Nessun valore da salvare");
            return;
        }
        
        // Verifica che tutti i fattori di conversione siano compilati
        for (let i = 0; i < $data.response.length; i++) {
            if (!$data.response[i].rispostaValore) {
                failure("Il campo 'Valore Risposta' non può essere vuoto");
                return;
            }
        }
        
        [dataSubmit, loadingSubmit, errorSubmit] = ValoreRispostaQuestionario.saveMultiple($data);
    }
    
    // Monitora lo stato del salvataggio per mostrare un messaggio di successo
    $: if ($dataSubmit && !$loadingSubmit && !$errorSubmit) {
        success("Valori salvati con successo");
    }
    
    function backToQuestionario() {
        push(`/questionario/${params.questionarioId}`);
    }

    // Funzione per caricare i valori ereditati
    async function loadInheritedValues() {
        // Prima verifica se ci sono valori personalizzati nel database
        const personalizedResponse = await fetch(`api/valorerisposta/elenco`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                questionarioId: parseInt(params.questionarioId),
                tipoValore: params.tipo
            })
        });
        const personalizedResult = await personalizedResponse.json();
        
        // Se ci sono valori personalizzati (con isOverride = true), usa quelli
        if (personalizedResult.response && personalizedResult.response.some(v => v.isOverride)) {
            $data.response = personalizedResult.response;
            usingInherited = false;
            return;
        }
        
        // Altrimenti procedi con i valori ereditati come fai ora
        const response = await fetch(`api/valorerisposta/inherited/${params.questionarioId}/${params.tipo}`);
        const result = await response.json();
        if (result.isError) {
            failure("Errore nel caricamento dei valori ereditati");
            return;
        }
        
        valoriEreditati = result.response || [];
        usingInherited = true;
        
        // Estrai il nome della domanda dai valori ereditati (se disponibile)
        if (valoriEreditati.length > 0) {
            domandaNome = valoriEreditati[0].domandaNome || '';
        }
    }

    $: if ($dataQuestionario && !$loadingQuestionario) {
        if ($dataQuestionario.isMaster) {
            usingInherited = false;
            if (!opzioniCaricate) {
                caricaOpzioniQuestionario();
            }
        } else if (!usingInherited && $data && !opzioniCaricate) {
            caricaOpzioniQuestionario();
        }
    }

    $: if (opzioniCaricate && Object.keys(opzioniQuestionario).length > 0 && $data && $data.response && $data.response.length === 0) {
        inizializzaValoriDaOpzioni();
    }
    
    onMount(async () => {
        // Se è un questionario master, non dovrebbe ereditare valori
        if ($dataQuestionario && $dataQuestionario.isMaster) {
            usingInherited = false;
        } else {
            // Altrimenti carica i valori ereditati
            await loadInheritedValues();
        }
        
        // Carica sempre le opzioni, indipendentemente dal tipo di questionario
        await caricaOpzioniQuestionario();
    });
    
    // Funzione per passare dalla modalità ereditata a quella personalizzata
    function customizeValues() {
        if (usingInherited) {
            // Se le opzioni non sono state caricate, caricale
            if (!opzioniCaricate) {
                caricaOpzioniQuestionario();
            }
            
            // Se ci sono valori ereditati, usali come base
            if (valoriEreditati.length > 0) {
                $data.response = valoriEreditati.map(v => new ValoreRispostaQuestionario({
                    id: 0,
                    questionarioId: parseInt(params.questionarioId),
                    domandaNome: v.domandaNome,
                    tipoValore: params.tipo,
                    rispostaValore: v.rispostaValore,
                    fattoreConversione: v.fattoreConversione,
                    isOverride: true
                }));
                
                // Estrai il nome della domanda 
                if (valoriEreditati.length > 0) {
                    domandaNome = valoriEreditati[0].domandaNome || '';
                }
            }
            
            usingInherited = false;
        }
    }
    
    // Funzione per ripristinare l'ereditarietà
    function resetToInherited() {
        openConfirmResetDialog = true;
    }
    
    function confirmResetToInherited() {
        if (!usingInherited) {
            // Elimina tutti i valori personalizzati
            [dataSubmit, loadingSubmit, errorSubmit] = ValoreRispostaQuestionario.deleteAll({
                questionarioId: parseInt(params.questionarioId),
                tipoValore: params.tipo
            });
            
            usingInherited = true;
            $data.response = [];
        }
    }
</script>

<!-- Modal di conferma per il ripristino valori ereditati -->
<ConfirmModal 
    content="<p class='font-bold'>Sei sicuro di voler ripristinare i valori ereditati?</p>
            <p class='text-sm'>Questa operazione eliminerà tutti i valori personalizzati e tornerà a utilizzare quelli ereditati dal questionario master/padre.</p>"
    bind:showModal="{openConfirmResetDialog}" 
    onConfirm="{confirmResetToInherited}" 
/>

<Card class="md:px-8 md:py-8" padded>
    <div class="flex justify-between">
        <TitleCard title="{params.tipo === 'SmartWorking' ? 'Configura valori Smart Working' : 'Configura valori PSCL'}" />
        <div class="sm:flex-nowrap sm:space-x-4 flex flex-wrap">
            <button type="button" class="btn-white h-14 w-14 sm:h-16 sm:w-16" on:click={backToQuestionario}>
                <span class="material-icons-outlined text-2xl leading-[3rem] sm:text-3xl sm:leading-[3.25rem]">arrow_back</span>
            </button>
            {#if $hasRoleSysAdmin}
                <button type="button" class="btn-white h-14 w-14 sm:h-16 sm:w-16" on:click={submit} disabled={usingInherited || !$data?.response || $data.response.length === 0}>
                    <span class="material-icons-outlined text-2xl leading-[3rem] sm:text-3xl sm:leading-[3.25rem]">save</span>
                </button>
            {/if}
        </div>
    </div>

    <!-- Banner informativo con stile coerente -->
    <div class="md:gap-x-8 md:px-4 grid grid-cols-1 pt-4">
        <div class="p-4 rounded border {$dataQuestionario && $dataQuestionario.isMaster ? 'bg-blue-50 border-blue-200' : usingInherited ? 'bg-gray-50 border-gray-200' : 'bg-green-50 border-green-200'}">
            {#if $dataQuestionario && $dataQuestionario.isMaster}
                <div class="flex items-center">
                    <span class="material-icons-outlined mr-2 text-blue-600">info</span>
                    <p>Questo è un questionario master. I valori qui configurati saranno ereditati dai questionari derivati.</p>
                </div>
            {:else if usingInherited}
                <div class="flex items-center justify-between">
                    <div class="flex items-center">
                        <span class="material-icons-outlined mr-2 text-gray-600">info</span>
                        <p>Stai utilizzando i valori ereditati dal questionario master/padre.</p>
                    </div>
                    <Button 
                        variant="raised" 
                        on:click={customizeValues}
                        disabled={valoriEreditati.length === 0}
                    >
                        <Label>Personalizza valori</Label>
                    </Button>
                </div>
            {:else}
                <div class="flex items-center justify-between">
                    <div class="flex items-center">
                        <span class="material-icons-outlined mr-2 text-green-600">edit</span>
                        <p>Stai personalizzando i valori per questo questionario specifico.</p>
                    </div>
                    <Button 
                        variant="raised" 
                        on:click={resetToInherited}
                    >
                        <Label>Ripristina valori ereditati</Label>
                    </Button>
                </div>
            {/if}
        </div>
    </div>
    
    {#if ($dataQuestionario && $dataQuestionario.isMaster) || !usingInherited}
        {#if $loading || $loadingQuestionario || $loadingSubmit || loadingOpzioni}
            <FormLoading customClass="pt-8" />
        {:else}
            <div class="md:px-4 grid grid-cols-1 pt-4">
                <div class="mb-2 text-lg font-semibold">Questionario: {$dataQuestionario?.descrizione || ''}</div>
                <Card padded>
                    <div class="p-4">
                        <h3 class="mb-2 text-lg font-bold">{params.tipo === 'SmartWorking' ? 'Configurazione valori Smart Working' : 'Configurazione Valori PSCL'}</h3>
                        <p class="mb-2 text-gray-700">
                            {#if params.tipo === 'SmartWorking'}
                                Configura i valori numerici da associare alle risposte della domanda flaggata come "Smart Working".
                                Questi valori saranno utilizzati nel calcolo delle emissioni risparmiate.
                            {:else}
                                Configura i valori numerici da associare alle risposte della domanda flaggata come "PSCL".
                                Questi valori saranno utilizzati nel calcolo delle emissioni risparmiate.
                            {/if}
                        </p>
                        
                        {#if params.tipo === 'SmartWorking'}
                            <div class="mt-2 text-sm text-gray-600">
                                <p>Esempi di valori per Smart Working:</p>
                                <ul class="pl-6 list-disc">
                                    <li>Mai o comunque meno di un giorno a settimana = 0</li>
                                    <li>Un giorno = 0,2</li>
                                    <li>2 giorni = 0,4</li>
                                    <li>3 giorni = 0,6</li>
                                    <li>4 giorni = 0,8</li>
                                    <li>5 giorni = 1</li>
                                </ul>
                            </div>
                        {:else}
                            <div class="mt-2 text-sm text-gray-600">
                                <p>Esempi di valori per PSCL:</p>
                                <ul class="pl-6 list-disc">
                                    <li>Auto privata = 1</li>
                                    <li>Auto aziendale = 1</li>
                                    <li>Altre modalità = 0</li>
                                </ul>
                            </div>
                        {/if}
                    </div>
                </Card>
                
                {#if Object.keys(opzioniQuestionario).length > 0}
                    <div class="bg-green-50 flex items-center p-3 mt-4 mb-4 border border-green-200 rounded">
                        <span class="material-icons-outlined mr-2 text-green-600">auto_awesome</span>
                        <span>Sono state caricate automaticamente <b>{Object.keys(opzioniQuestionario).length} opzioni</b> di risposta dal questionario.</span>
                    </div>
                {/if}
                
                <div class="overflow-x-auto">
                    <table class="min-w-full p-0 m-0 text-sm leading-10 border-t-4 border-collapse border-black table-auto">
                        <thead>
                            <tr class="p-1">
                                <th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">Valore Risposta</th>
                                <th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">Testo Display</th>
                                <th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">Fattore di Conversione</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#if loadingOpzioni}
                                <tr>
                                    <td colspan="3" class="border-b p-2.5 text-center leading-5">
                                        <div class="flex items-center justify-center">
                                            <div class="animate-spin w-6 h-6 mr-3 border-b-2 border-blue-500 rounded-full"></div>
                                            <span>Caricamento opzioni dal questionario...</span>
                                        </div>
                                    </td>
                                </tr>
                            {:else if $data?.response && $data.response.length > 0}
                                {#each $data.response as valore, i}
                                    <tr class="odd:bg-white even:bg-gray-50 p-1 {opzioniQuestionario[valore.rispostaValore] ? 'bg-blue-50' : ''}">
                                        <td class="border-b p-2.5 text-center leading-5">
                                            {#if opzioniQuestionario[valore.rispostaValore]}
                                                <div class="flex items-center justify-center">
                                                    <span class="material-icons-outlined mr-2 text-sm text-blue-500">data_object</span>
                                                    {valore.rispostaValore}
                                                </div>
                                            {:else}
                                                <Input class="w-full" bind:value={valore.rispostaValore} />
                                            {/if}
                                        </td>
                                        <td class="border-b p-2.5 text-center leading-5">
                                            {#if opzioniQuestionario[valore.rispostaValore]}
                                                {opzioniQuestionario[valore.rispostaValore]}
                                            {:else}
                                                <span class="italic text-gray-500">Non definito</span>
                                            {/if}
                                        </td>
                                        <td class="border-b p-2.5 text-center leading-5">
                                            <Input type="number" input$step="0.1" bind:value={valore.fattoreConversione} input$min="0" input$max="1" class="w-full" />
                                        </td>
                                    </tr>
                                {/each}
                            {:else}
                                <tr>
                                    <td colspan="3" class="border-b p-2.5 text-center leading-5 text-gray-500">
                                        Nessun valore configurato.
                                    </td>
                                </tr>
                            {/if}
                        </tbody>
                    </table>
                </div>
                
                <div class="mt-6 text-sm text-gray-600">
                    <p class="font-semibold">Note:</p>
                    <ul class="pl-6 mt-2 list-disc">
                        <li>Se un'opzione di risposta non è presente in questa configurazione, il suo valore sarà considerato automaticamente zero.</li>
                        <li>Il fattore di conversione deve essere un numero compreso tra 0 e 1.</li>
                    </ul>
                </div>
            </div>
        {/if}
    {:else}
        <div class="md:px-4 grid grid-cols-1 pt-4">
            <div class="overflow-x-auto">
                <table class="min-w-full p-0 m-0 text-sm leading-10 border-t-4 border-collapse border-black table-auto">
                    <thead>
                        <tr class="p-1">
                            <th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">Valore Risposta</th>
                            <th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">Testo Display</th>
                            <th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">Fattore di Conversione</th>
                            <th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider w-20">Fonte</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#if valoriEreditati && valoriEreditati.length > 0}
                            {#each valoriEreditati as valore}
                                <tr class="odd:bg-white even:bg-gray-50 p-1">
                                    <td class="border-b p-2.5 text-center leading-5">{valore.rispostaValore}</td>
                                    <td class="border-b p-2.5 text-center leading-5">
                                        {#if opzioniQuestionario[valore.rispostaValore]}
                                            {opzioniQuestionario[valore.rispostaValore]}
                                        {:else}
                                            <span class="italic text-gray-500">Non disponibile</span>
                                        {/if}
                                    </td>
                                    <td class="border-b p-2.5 text-center leading-5">{valore.fattoreConversione}</td>
                                    <td class="border-b p-2.5 text-center leading-5">
                                        <span class="text-sm text-blue-500">Ereditato</span>
                                    </td>
                                </tr>
                            {/each}
                        {:else}
                            <tr>
                                <td colspan="4" class="border-b p-2.5 text-center leading-5 text-gray-500">
                                    Nessun valore ereditato disponibile.
                                </td>
                            </tr>
                        {/if}
                    </tbody>
                </table>
            </div>
        </div>
    {/if}
</Card>